// MobileStockCount — offline-first stock count entry.
// Path: keyboard-driven barcode/SKU input + qty + reason. Full barcode-camera scanner
// (zxing-js) lands in Phase E.1 once we install the dep; this MVP works for the
// "basement walk-in with no signal" acceptance gate by accepting typed SKUs and
// queueing each adjustment as POST /api/mobile/inventory/update.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, ArrowLeft, Search, Plus, Minus, CheckCircle2, Loader2, ScanBarcode
} from 'lucide-react';
import MobileOfflineBanner from './MobileOfflineBanner';
import { queueOrSend } from '../utils/syncQueue';
import { readSharedToken } from '../utils/authTokenKeys';
import { scopeHeaders } from '../utils/scopeHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { value: 'count',       label: 'Count reconcile' },
  { value: 'waste',       label: 'Waste / spoilage' },
  { value: 'prep_loss',   label: 'Prep loss' },
  { value: 'received',    label: 'Goods received' },
  { value: 'adjustment',  label: 'Adjustment' },
];

const MobileStockCount = () => {
  const [items, setItems] = useState([]);                // org inventory list (loaded online, cached)
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('count');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const searchRef = useRef(null);

  // Load inventory items (best-effort online). Cached in localStorage for offline sessions.
  useEffect(() => {
    const cached = localStorage.getItem('bohStockItemsCache');
    if (cached) {
      try { setItems(JSON.parse(cached)); } catch { /* ignore */ }
    }
    // Was localStorage.getItem('token') || localStorage.getItem('mobileToken')
    // — never checked auth_token (audit-2026-09-07 §2, same bug syncQueue.js had).
    const token = readSharedToken();
    if (!token || !navigator.onLine) return;
    fetch(`${API_URL}/api/mobile/inventory`, { headers: scopeHeaders(token) })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (Array.isArray(list)) {
          setItems(list);
          try { localStorage.setItem('bohStockItemsCache', JSON.stringify(list.slice(0, 500))); } catch { /* ignore */ }
        }
      })
      .catch(() => {});
  }, []);

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return items.slice(0, 20);
    return items.filter(i =>
      (i.name || '').toLowerCase().includes(q) ||
      (i.sku || '').toLowerCase().includes(q) ||
      (i.barcode || '').toLowerCase().includes(q)
    ).slice(0, 20);
  })();

  const selected = items.find(i => (i.id || i._id) === selectedId);

  const bump = (delta) => {
    setQty(prev => {
      const cur = parseFloat(prev);
      const base = Number.isFinite(cur) ? cur : 0;
      return String(Math.max(0, base + delta));
    });
  };

  const submit = async () => {
    if (!selected) return;
    const qNum = parseFloat(qty);
    if (!Number.isFinite(qNum) || qNum === 0) return;

    setSubmitting(true);
    setFeedback(null);

    // Backend POST /api/mobile/inventory/update expects { itemId, change, reason }.
    // We treat the entered number as a positive count adjustment when reason=count|received,
    // negative for waste/prep_loss. For "count reconcile" we let the user enter a relative
    // change (+ to add, - to subtract). Negative numbers are entered via the − button.
    const change = (reason === 'waste' || reason === 'prep_loss') ? -Math.abs(qNum) : qNum;

    const res = await queueOrSend({
      method: 'POST',
      path: '/api/mobile/inventory/update',
      body: {
        itemId: selected.id || selected._id,
        change,
        reason,
      },
    });

    setSubmitting(false);
    const row = {
      ts: new Date().toISOString(),
      name: selected.name,
      change,
      reason,
      status: res.ok ? 'synced' : res.queued ? 'queued' : 'error',
    };
    setRecentSubmissions(prev => [row, ...prev].slice(0, 10));

    if (res.ok) {
      setFeedback({ kind: 'ok', msg: `${selected.name}: ${change > 0 ? '+' : ''}${change}` });
    } else if (res.queued) {
      setFeedback({ kind: 'queued', msg: `Offline — saved locally (${selected.name}: ${change > 0 ? '+' : ''}${change})` });
    } else {
      setFeedback({ kind: 'error', msg: res.error || 'Failed to update stock' });
    }
    setQty('');
    if (searchRef.current) searchRef.current.focus();
  };

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="boh-stock-count-page">
      <MobileOfflineBanner />
      <header className="bg-surface border-b border-surface-border px-4 py-3 flex items-center gap-3">
        <Link to="/mobile/boh" className="p-2 -ml-2 rounded-md hover:bg-surface-sunken" data-testid="boh-stock-back">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-overline text-ink-muted">Back of House</div>
          <h1 className="font-display text-lg text-ink flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary-500" /> Stock Count</h1>
        </div>
      </header>

      <div className="px-4 py-5 space-y-5 pb-24">
        {/* Search / barcode entry */}
        <div className="bg-surface border border-surface-border rounded-lg p-3">
          <div className="flex items-center gap-2">
            <ScanBarcode className="w-4 h-4 text-ink-muted" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Scan or type SKU / name / barcode"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-2 py-2 bg-transparent outline-none text-sm"
              data-testid="boh-stock-search"
              autoFocus
            />
            <Search className="w-4 h-4 text-ink-subtle" />
          </div>
          {filtered.length > 0 ? (
            <ul className="mt-2 max-h-60 overflow-y-auto divide-y divide-surface-border" data-testid="boh-stock-result-list">
              {filtered.map(it => {
                const id = it.id || it._id;
                const active = id === selectedId;
                return (
                  <li key={id}>
                    <button
                      onClick={() => setSelectedId(id)}
                      className={`w-full text-left px-2 py-2 rounded-md ${active ? 'bg-primary-50' : 'hover:bg-surface-sunken'}`}
                      data-testid={`boh-stock-pick-${id}`}
                    >
                      <div className="text-sm font-medium text-ink">{it.name}</div>
                      <div className="text-xs text-ink-muted font-data tabular-nums">
                        {it.sku ? `${it.sku} · ` : ''}{it.unit || ''}{typeof it.quantity !== 'undefined' ? ` · on hand ${it.quantity}` : ''}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : items.length === 0 ? (
            <p className="mt-2 text-xs text-ink-muted px-1" data-testid="boh-stock-no-items">
              No items cached yet. Go online once to pull your inventory list.
            </p>
          ) : (
            <p className="mt-2 text-xs text-ink-muted px-1">No match.</p>
          )}
        </div>

        {/* Qty + reason */}
        {selected ? (
          <div className="bg-surface border border-surface-border rounded-lg p-4" data-testid="boh-stock-entry-card">
            <div className="text-xs uppercase tracking-overline text-ink-muted">Selected</div>
            <div className="font-display text-lg text-ink mt-1">{selected.name}</div>

            <label className="text-xs uppercase tracking-overline text-ink-muted mt-4 block">Change (qty)</label>
            <div className="mt-2 flex items-center gap-2">
              <button onClick={() => bump(-1)} className="w-12 h-12 rounded-md border border-surface-border bg-surface" data-testid="boh-stock-qty-minus">
                <Minus className="w-5 h-5 inline" />
              </button>
              <input aria-label="Change (qty)"
                type="number"
                step="1"
                inputMode="decimal"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="0"
                className="flex-1 px-3 py-3 text-2xl font-data tabular-nums text-center bg-surface-muted border border-surface-border rounded-md focus:outline-none focus:border-primary-500"
                data-testid="boh-stock-qty-input"
              />
              <button onClick={() => bump(1)} className="w-12 h-12 rounded-md border border-surface-border bg-surface" data-testid="boh-stock-qty-plus">
                <Plus className="w-5 h-5 inline" />
              </button>
            </div>

            <label className="text-xs uppercase tracking-overline text-ink-muted mt-4 block">Reason</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {REASONS.map(r => (
                <button
                  key={r.value}
                  onClick={() => setReason(r.value)}
                  className={`px-3 py-2 text-sm rounded-md border motion-organic ${
                    reason === r.value
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-surface border-surface-border text-ink hover:bg-surface-sunken'
                  }`}
                  data-testid={`boh-stock-reason-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <button
              disabled={submitting || qty === ''}
              onClick={submit}
              className="mt-4 w-full bg-primary-500 text-surface py-3 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-40 motion-organic"
              data-testid="boh-stock-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : null}
              Save adjustment
            </button>
          </div>
        ) : null}

        {feedback ? (
          <div
            className={`rounded-md px-3 py-2 text-sm flex items-start gap-2 ${
              feedback.kind === 'ok' ? 'bg-ok/10 text-ok'
              : feedback.kind === 'queued' ? 'bg-warn/10 text-warn'
              : 'bg-danger/10 text-danger'
            }`}
            data-testid="boh-stock-feedback"
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5" /> {feedback.msg}
          </div>
        ) : null}

        {recentSubmissions.length > 0 ? (
          <div className="bg-surface border border-surface-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 text-xs uppercase tracking-overline text-ink-muted border-b border-surface-border">This shift</div>
            <ul>
              {recentSubmissions.map((r, i) => (
                <li key={i} className="px-3 py-2 border-b border-surface-border last:border-0 flex items-center justify-between text-sm" data-testid={`boh-stock-recent-${i}`}>
                  <span className="text-ink truncate flex-1">{r.name}</span>
                  <span className={`font-data tabular-nums ${r.change > 0 ? 'text-ok' : 'text-danger'}`}>
                    {r.change > 0 ? '+' : ''}{r.change}
                  </span>
                  <span className="text-xs text-ink-muted ml-3">{r.reason}</span>
                  <span className={`text-xs ml-3 ${r.status === 'synced' ? 'text-ok' : r.status === 'queued' ? 'text-warn' : 'text-danger'}`}>
                    {r.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MobileStockCount;
